import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import {
  AlertTriangle,
  RefreshCw,
  Calendar,
  ClipboardList,
  Users,
  Briefcase,
  MessageSquare,
  Scale,
  FileText,
  ArrowLeft,
  Check,
  XCircle,
  Clock,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/App";

// Sanitize HTML content to prevent XSS attacks
const sanitizeHtml = (html) => {
  if (!html) return "";
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
};

const iconMap = {
  AlertTriangle,
  RefreshCw,
  Calendar,
  ClipboardList,
  Users,
  Briefcase,
  MessageSquare,
  Scale,
  FileText,
};

export default function PillarDetail() {
  const { slug } = useParams();
  const [pillar, setPillar] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pillarRes, protocolsRes] = await Promise.all([
          api.get(`/pillars/${slug}`),
          api.get("/protocols"),
        ]);
        setPillar(pillarRes.data);
        // Filter protocols for this pillar
        const filteredProtocols = protocolsRes.data.filter(
          (p) => p.pillar_id === pillarRes.data.id
        );
        setProtocols(filteredProtocols);
      } catch (error) {
        // Error fetching pillar data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!pillar) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Pillar Not Found</h1>
          <Link to="/framework">
            <Button>Back to Framework</Button>
          </Link>
        </div>
      </div>
    );
  }

  const Icon = iconMap[pillar.icon] || FileText;

  return (
    <div data-testid="pillar-detail-page">
      {/* Header */}
      <section 
        className="py-16 lg:py-24 text-white"
        style={{ backgroundColor: pillar.color }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/framework" 
            className="inline-flex items-center text-white/80 hover:text-white mb-6"
            data-testid="back-to-framework"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Framework
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-6"
          >
            <div className="w-16 h-16 bg-white/20 rounded-sm flex items-center justify-center flex-shrink-0">
              <Icon className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-mono text-white/70 uppercase mb-2">
                Pillar {pillar.order}
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="pillar-title">
                {pillar.title}
              </h1>
              <p className="text-lg text-white/90 max-w-2xl">
                {pillar.description}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Purpose */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Purpose</h2>
                <div 
                  className="text-slate-600 text-lg prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(pillar.purpose) }}
                />
              </div>

              {/* Non-Negotiables */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">
                  Non-Negotiables
                </h2>
                <div className="space-y-3" data-testid="non-negotiables">
                  {pillar.non_negotiables?.map((item) => (
                    <motion.div
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-100 rounded-sm"
                    >
                      <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Common Failure Points */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">
                  Common Failure Points
                </h2>
                <div className="space-y-3" data-testid="failure-points">
                  {pillar.failure_points?.map((item) => (
                    <motion.div
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-sm"
                    >
                      <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Related Protocols */}
              {protocols.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">
                    Related Protocols
                  </h2>
                  <div className="space-y-3" data-testid="related-protocols">
                    {protocols.map((protocol) => (
                      <Link
                        key={protocol.id}
                        to={`/protocols/${protocol.slug}`}
                        className="block p-4 bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-300"
                        data-testid={`protocol-link-${protocol.slug}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`badge-urgency-${protocol.urgency} px-2 py-0.5 rounded-full text-xs font-mono uppercase`}>
                                {protocol.urgency}
                              </span>
                            </div>
                            <h3 className="font-bold text-slate-900">
                              {protocol.title}
                            </h3>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Recommended Cadence */}
                {pillar.recommended_cadence && (
                  <div className="bg-slate-50 border border-slate-200 p-6 rounded-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-5 h-5 text-slate-500" />
                      <h3 className="font-bold text-slate-900">Recommended Cadence</h3>
                    </div>
                    <p className="text-sm text-slate-600">
                      {pillar.recommended_cadence}
                    </p>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="bg-white border border-slate-200 p-6 rounded-sm">
                  <h3 className="font-bold text-slate-900 mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <Link to="/protocols" className="block">
                      <Button variant="outline" className="w-full justify-start" data-testid="browse-all-protocols-btn">
                        Browse All Protocols
                      </Button>
                    </Link>
                    <Link to="/toolkits" className="block">
                      <Button variant="outline" className="w-full justify-start" data-testid="view-toolkits-btn">
                        View Toolkits
                      </Button>
                    </Link>
                    <Link to="/data" className="block">
                      <Button variant="outline" className="w-full justify-start" data-testid="data-guides-btn">
                        Data Guides
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
